import { registerView } from '../viewRegistry';
import { interactiveRegistry, navigateTo } from '../interactivity';
import { getCanonicalSafeArea } from '../safeArea';
import { renderBlobBackground } from '../renderer/plasma';
import { DopeTextRenderer } from '../renderer/bitmapFont';
import useCoreSession, { coreErrorMessage } from '../../stores/coreSession';
import DopeUI, { TERMINAL } from '../ui/DopeOS-UI';

const state = { events: [], loaded: false, loading: false, selected: 0, error: null };

function text(ctx, value, x, y, color = TERMINAL.TEXT) {
  ctx.fillStyle = color;
  DopeTextRenderer.renderDOSText(ctx, value, x, y, 1);
}

function rule(ctx, x, y, w, color = TERMINAL.GREEN_DARK) {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, 1);
}

async function loadEvents() {
  if (state.loading) return;
  state.loading = true;
  state.error = null;
  try {
    state.events = await useCoreSession.getState().loadMarketEvents();
    state.loaded = true;
    if (state.selected >= state.events.length) state.selected = 0;
  } catch (error) {
    state.error = coreErrorMessage(error);
  } finally {
    state.loading = false;
  }
}

function drawTop(ctx, width) {
  const session = useCoreSession.getState();
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, width, 26);
  rule(ctx, 0, 25, width);
  text(ctx, 'DOPEOS // SIGNALS', 8, 5, TERMINAL.TEXT);
  if (width >= 500) text(ctx, 'DEAD DROPS // MARKET EVENTS', 224, 5, TERMINAL.TEXT_DIM);
  text(ctx, session.status === 'online' ? 'LIVE' : 'OFFLINE', width - 64, 5, session.status === 'online' ? TERMINAL.GREEN : TERMINAL.TEXT_DIM);
}

function drawList(ctx, x, y, w, h) {
  DopeUI.Panel(ctx, x, y, w, h, 'ACTIVE EVENTS');
  if (!state.events.length) {
    text(ctx, state.error || (state.loading ? 'SYNCING...' : 'NO SIGNALS IN RANGE.'), x + 12, y + 34, state.error ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT_DIM);
    return;
  }
  state.events.forEach((event, index) => {
    const rowY = y + 32 + index * 42;
    if (rowY + 34 > y + h - 10) return;
    const selected = index === state.selected;
    if (selected) {
      ctx.fillStyle = TERMINAL.GREEN_DARK;
      ctx.fillRect(x + 6, rowY - 4, w - 12, 36);
    }
    text(ctx, `${selected ? '>' : ' '} ${String(event.kind || 'EVENT').toUpperCase()}`, x + 12, rowY, selected ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT);
    text(ctx, `${String(event.assetId || '--').toUpperCase()} // ${String(event.districtId || '--').toUpperCase()}`, x + 20, rowY + 16, TERMINAL.TEXT_DIM);
    interactiveRegistry.register({
      id: `signal-${event.eventId}`,
      x: x + 6,
      y: rowY - 4,
      w: w - 12,
      h: 36,
      onClick: () => { state.selected = index; },
    });
  });
}

function drawDetail(ctx, x, y, w, h) {
  DopeUI.Panel(ctx, x, y, w, h, 'SIGNAL DETAIL');
  const event = state.events[state.selected];
  if (!event) {
    text(ctx, 'SELECT A SIGNAL.', x + 12, y + 30, TERMINAL.TEXT_DIM);
    return;
  }
  const multiplier = Number(event.multiplierBps || 10000) / 10000;
  const rows = [
    ['EVENT', String(event.eventId || '--')],
    ['KIND', String(event.kind || '--').toUpperCase()],
    ['ASSET', String(event.assetId || '--').toUpperCase()],
    ['DISTRICT', String(event.districtId || '--').toUpperCase()],
    ['MULTIPLIER', `x${multiplier.toFixed(2)}`],
    ['STATUS', String(event.status || '--').toUpperCase()],
    ['TURN', String(event.activeTurn ?? '--')],
    ['SOURCE', String(event.source || '--').toUpperCase()],
  ];
  let cursor = y + 28;
  rows.forEach(([label, value]) => {
    text(ctx, label, x + 12, cursor, TERMINAL.TEXT_DIM);
    text(ctx, value, x + 116, cursor, TERMINAL.TEXT);
    cursor += 18;
  });
  rule(ctx, x + 12, cursor + 2, w - 24);
  text(ctx, 'RELIABILITY IS NOT GUARANTEED.', x + 12, cursor + 16, TERMINAL.TEXT_DIM);
  text(ctx, 'VERIFY BEFORE YOU MOVE.', x + 12, cursor + 34, TERMINAL.GREEN);
}

registerView('signals', function renderSignals(ctx, time, width, height) {
  interactiveRegistry.clear();
  const safe = getCanonicalSafeArea({ w: width || 640, h: height || 400 });
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, safe.height);
  ctx.save();
  ctx.globalAlpha = 0.12;
  renderBlobBackground(ctx, time, safe.width, safe.height);
  ctx.restore();

  if (!state.loaded && !state.loading && useCoreSession.getState().status === 'online') loadEvents();

  drawTop(ctx, safe.width);
  const mobile = safe.width < 500;
  if (mobile) {
    drawList(ctx, 8, 32, safe.width - 16, 250);
    drawDetail(ctx, 8, 290, safe.width - 16, 230);
  } else {
    drawList(ctx, 8, 36, 248, 280);
    drawDetail(ctx, 264, 36, 368, 280);
  }

  const footerY = mobile ? 544 : 328;
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, footerY, safe.width, safe.height - footerY);
  rule(ctx, 0, footerY, safe.width);
  text(ctx, 'TAB FOCUS   ENTER SELECT   ESC DESKTOP', 8, mobile ? footerY + 8 : 360, TERMINAL.TEXT_DIM);
  const refreshX = mobile ? 8 : 8;
  const refreshY = mobile ? 600 : 336;
  const refreshW = mobile ? 120 : 100;
  DopeUI.Button(ctx, refreshX, refreshY, refreshW, 24, state.loading ? 'SYNC' : 'REFRESH', state.loading ? 'disabled' : 'normal');
  interactiveRegistry.register({
    id: 'signals-refresh', x: refreshX, y: refreshY, w: refreshW, h: 24,
    onClick: () => { state.loaded = false; loadEvents(); },
  });
  const backX = mobile ? safe.width - 168 : safe.width - 200;
  const backW = mobile ? 160 : 192;
  DopeUI.Button(ctx, backX, refreshY, backW, 24, 'BACK TO DESKTOP', 'normal');
  interactiveRegistry.register({
    id: 'signals-back', x: backX, y: refreshY, w: backW, h: 24,
    onClick: () => navigateTo('dopeos_home'),
  });
  if (!mobile) text(ctx, 'SIGNALS // WHAT THE STREET KNOWS.', 8, 384, TERMINAL.GREEN);
});
